import React, { useRef } from 'react';
import useStudentMaterials from '../hooks/useStudentMaterials';
import './MaterialsSection.css';

/** Display icon from the file's mime (independent of the stored `type` enum,
 *  so an image reads as an image even though it's stored under 'other'). */
const iconFor = (m) => {
	const ft = m.fileType || '';
	if (ft.startsWith('audio/')) return 'fa-file-audio';
	if (ft.startsWith('video/')) return 'fa-file-video';
	if (ft.startsWith('image/')) return 'fa-file-image';
	if (ft === 'application/pdf') return 'fa-file-pdf';
	return 'fa-file-lines';
};

/**
 * Materials — file rows + a one-tap upload row, in the drawer (phone) or the
 * rail (iPad+, Ph 6). Rows open the file in a new tab; rename/retype/delete live
 * on the full student profile. Upload infers title + type; see useStudentMaterials.
 */
const MaterialsSection = ({ studentId, studentName }) => {
	const { materials, loading, uploading, progress, error, upload } = useStudentMaterials(
		studentId,
		studentName
	);
	const fileRef = useRef(null);

	const pick = () => fileRef.current?.click();
	const onFile = (e) => {
		const file = e.target.files?.[0];
		if (file) upload(file);
		e.target.value = ''; // allow re-picking the same file
	};

	return (
		<section className="ld-materials">
			<h3 className="ld-drawer__eyebrow">Materials</h3>

			{loading ? (
				<p className="ld-materials__empty">Loading…</p>
			) : materials.length === 0 ? (
				<p className="ld-materials__empty">Nothing uploaded yet.</p>
			) : (
				<ul className="ld-materials__list">
					{materials.map((m) => (
						<li key={m.id}>
							<a
								className="ld-materials__row"
								href={m.downloadURL}
								target="_blank"
								rel="noopener noreferrer">
								<i className={`fa-sharp fa-solid ${iconFor(m)}`} aria-hidden="true" />
								<span className="ld-materials__title">{m.title || m.fileName}</span>
								<i
									className="fa-sharp fa-solid fa-arrow-up-right-from-square ld-materials__open"
									aria-hidden="true"
								/>
							</a>
						</li>
					))}
				</ul>
			)}

			<input
				ref={fileRef}
				type="file"
				accept=".pdf,.mp3,.wav,.m4a,.mp4,.mov,.doc,.docx,.png,.jpg,.jpeg"
				onChange={onFile}
				hidden
			/>
			<button
				type="button"
				className="ld-materials__upload"
				onClick={pick}
				disabled={uploading}>
				<i className="fa-sharp fa-solid fa-arrow-up-from-bracket" aria-hidden="true" />
				{uploading ? `Uploading… ${Math.round(progress)}%` : 'Upload — audio, PDF, images'}
			</button>

			{error && (
				<p className="ld-materials__error" role="alert">
					{error}
				</p>
			)}
		</section>
	);
};

export default MaterialsSection;
